// UiToolRenderer (spec-9, ac-28/ac-29): one place that turns a model UI tool_use
// into a transcript widget. Eight names, eight renderers. Six are display-only;
// two — render_choices and render_action_buttons — are interactive: they call
// onRespond with the operator's choice, which the loop turns into the tool_result
// that resumes the conversation. While unanswered they hold the loop paused; once
// answered (`resolved`) the controls disable so a choice can't be re-sent (ac-29).

import type { ReactNode } from "react";
import type { ToolUseBlock } from "../../agent/graph";
import { ChartBlock } from "./ChartBlock";

interface RendererProps {
  block: ToolUseBlock;
  /** True once an interactive tool has been answered — disables its controls. */
  resolved?: boolean;
  /** Interactive tools call this with the operator's choice. */
  onRespond?: (value: string) => void;
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

// Choices/buttons accept either ["a","b"] or [{label,value}] — normalize both.
interface Choice {
  label: string;
  value: string;
}
function toChoices(raw: unknown): Choice[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => {
    if (typeof c === "string") return { label: c, value: c };
    const r = asRecord(c);
    const label = String(r.label ?? r.value ?? "");
    return { label, value: String(r.value ?? r.label ?? label) };
  });
}

const CALLOUT_TONES: Record<string, string> = {
  info: "border-edge bg-surface text-secondary",
  success:
    "border-status-success-border bg-status-success-bg text-status-success-text",
  warning:
    "border-status-warning-border bg-status-warning-bg text-status-warning-text",
  danger:
    "border-status-danger-border bg-status-danger-bg text-status-danger-text",
};

export function UiToolRenderer({ block, resolved, onRespond }: RendererProps) {
  const input = asRecord(block.input);

  switch (block.name) {
    case "render_chart":
      return <ChartBlock input={block.input} />;

    case "render_table":
      return <TableBlock input={input} />;

    case "render_callout": {
      const tone = String(input.tone ?? "info");
      return (
        <div
          data-testid="ui-callout"
          data-tone={tone}
          role="note"
          className={`rounded-xl border px-4 py-3 text-sm ${
            CALLOUT_TONES[tone] ?? CALLOUT_TONES.info
          }`}
        >
          {input.title != null && (
            <div className="mb-1 font-medium text-heading">
              {String(input.title)}
            </div>
          )}
          {input.text != null && <p>{String(input.text)}</p>}
        </div>
      );
    }

    case "render_choices":
      return (
        <ChoiceGroup
          testid="ui-choices"
          prompt={input.prompt != null ? String(input.prompt) : undefined}
          choices={toChoices(input.choices ?? input.options)}
          variant="list"
          resolved={resolved}
          onRespond={onRespond}
        />
      );

    case "render_action_buttons":
      return (
        <ChoiceGroup
          testid="ui-action-buttons"
          prompt={input.prompt != null ? String(input.prompt) : undefined}
          choices={toChoices(input.buttons ?? input.actions ?? input.choices)}
          variant="buttons"
          resolved={resolved}
          onRespond={onRespond}
        />
      );

    case "render_steps": {
      const steps = Array.isArray(input.steps) ? input.steps : [];
      return (
        <ol
          data-testid="ui-steps"
          className="space-y-2 rounded-xl border border-edge bg-surface p-4 text-sm text-secondary"
        >
          {steps.map((s, i) => {
            const r = asRecord(s);
            const title = typeof s === "string" ? s : String(r.title ?? "");
            const detail = r.detail != null ? String(r.detail) : null;
            return (
              <li key={i} className="flex gap-2" data-testid="ui-step">
                <span className="flex-none font-medium text-heading">
                  {i + 1}.
                </span>
                <span>
                  <span className="text-primary">{title}</span>
                  {detail && <span className="block text-muted">{detail}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      );
    }

    case "render_quote":
      return (
        <blockquote
          data-testid="ui-quote"
          className="rounded-xl border-l-2 border-accent bg-surface px-4 py-3 text-sm italic text-secondary"
        >
          {input.text != null && <p>{String(input.text)}</p>}
          {input.cite != null && (
            <footer className="mt-1 text-xs not-italic text-muted">
              — {String(input.cite)}
            </footer>
          )}
        </blockquote>
      );

    case "render_progress": {
      const value = Number(input.value ?? 0);
      const max = Number(input.max ?? 100) || 100;
      const pct = Math.max(0, Math.min(100, (value / max) * 100));
      return (
        <div data-testid="ui-progress" className="text-sm text-secondary">
          {input.label != null && (
            <div className="mb-1 flex justify-between">
              <span>{String(input.label)}</span>
              <span className="text-muted">{Math.round(pct)}%</span>
            </div>
          )}
          <div className="h-2 w-full overflow-hidden rounded-full bg-card-hover">
            <div
              className="h-full rounded-full bg-accent"
              style={{ width: `${pct}%` }}
              data-testid="ui-progress-bar"
            />
          </div>
        </div>
      );
    }

    default:
      // Unknown UI tool — surface it rather than silently dropping the turn.
      return (
        <div
          data-testid="ui-unknown"
          className="rounded-xl border border-edge bg-surface px-4 py-3 text-sm text-muted"
        >
          Unsupported widget: {block.name}
        </div>
      );
  }
}

// The shared interactive widget for render_choices (a stacked list) and
// render_action_buttons (an inline row). Until answered it pauses the loop;
// answering disables every control so the choice is final (ac-29).
function ChoiceGroup({
  testid,
  prompt,
  choices,
  variant,
  resolved,
  onRespond,
}: {
  testid: string;
  prompt?: string;
  choices: Choice[];
  variant: "list" | "buttons";
  resolved?: boolean;
  onRespond?: (value: string) => void;
}): ReactNode {
  return (
    <div
      data-testid={testid}
      data-resolved={resolved ? "true" : "false"}
      className="rounded-xl border border-edge bg-surface p-4"
    >
      {prompt && <p className="mb-3 text-sm text-secondary">{prompt}</p>}
      <div className={variant === "buttons" ? "flex flex-wrap gap-2" : "space-y-2"}>
        {choices.map((c, i) => (
          <button
            key={`${c.value}-${i}`}
            type="button"
            data-testid="ui-choice"
            data-value={c.value}
            disabled={resolved}
            onClick={() => onRespond?.(c.value)}
            className={
              variant === "buttons"
                ? "rounded-lg border border-edge bg-page px-3 py-1.5 text-sm text-primary transition-colors hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                : "block w-full rounded-lg border border-edge bg-page px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
            }
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Display-only tabular result. Accepts `{ columns?, rows }` where rows are objects
// or arrays; columns default to the keys of the first row object.
function TableBlock({ input }: { input: Record<string, unknown> }) {
  const rows = Array.isArray(input.rows) ? input.rows : [];
  let columns: string[] = Array.isArray(input.columns)
    ? input.columns.map(String)
    : [];
  if (columns.length === 0 && rows.length > 0 && !Array.isArray(rows[0])) {
    columns = Object.keys(rows[0] as Record<string, unknown>);
  }

  return (
    <div
      data-testid="ui-table"
      className="overflow-x-auto rounded-xl border border-edge bg-surface"
    >
      <table className="w-full text-left text-sm">
        {columns.length > 0 && (
          <thead className="border-b border-edge text-xs uppercase tracking-wider text-muted">
            <tr>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2 font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, ri) => {
            const cells = Array.isArray(row)
              ? row
              : columns.map((c) => (row as Record<string, unknown>)[c]);
            return (
              <tr key={ri} className="border-b border-edge/50" data-testid="ui-table-row">
                {cells.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-secondary">
                    {cell == null ? "" : String(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
